import { useTranslation } from 'next-i18next';
import type { FC } from 'react';

import { NS } from '@/constants';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { useScreenSizeContext } from '@/components/ScreenSize.context';

import styles from './ModalHeader.ds.module.scss';
import { URLS } from '@/constants/urls';

type modalWithBothButtons = {
  whichButton: 'bothButtons';
  onReturnClick: () => void;
  onCloseClick: () => void;
  centeredTitle?: never;
  title: string;
  size?: 'small' | 'large';
}

type modalWithReturnButton = {
  whichButton: 'returnButton';
  onReturnClick: () => void;
  onCloseClick?: never;
  title?: never;
  centeredTitle?: never;
  size?: 'small' | 'large';
}

type modalWithCloseButton = {
  whichButton?: 'closeButton';
  onReturnClick?: never;
  onCloseClick: () => void;
  title?: string;
  centeredTitle?: boolean;
  size?: 'small' | 'large';
}

export type ModalHeaderDsProps = modalWithBothButtons | modalWithReturnButton | modalWithCloseButton;

export const ModalHeader: FC<ModalHeaderDsProps> = (
  { title, whichButton = 'closeButton', onReturnClick, onCloseClick, centeredTitle = false, size }
) => {
  const { t } = useTranslation([NS.ALTER]);
  const isMobile = useScreenSizeContext().isMobile;
  const withReturnButton = whichButton === 'returnButton';
  const withCloseButton = whichButton === 'closeButton';
  const withBothButtons = whichButton === 'bothButtons';

  const headerStyle = [
    styles.header,
    size ? size : isMobile ? '' : styles['extra-padding'],
    withReturnButton ? styles['no-close-button'] : ''
  ].join(' ');

  const titleStyle = [
    styles['title'],
    withBothButtons || withReturnButton || centeredTitle ? styles['centered-title'] : '',
    withCloseButton && !centeredTitle ? styles['with-close-button-only'] : ''
  ].join(' ');

  return (
    <div className={headerStyle}>
      {(withReturnButton || withBothButtons) && (
        <CallToActionContainer
          className={styles['return-button']}
          icon={{ url: URLS.ARROW_LEFT, alt: t('back') }}
          onClick={onReturnClick} kind="tertiary"
        />
      )}
      {title && (
        <span className={titleStyle}>{title}</span>
      )}
      {(withCloseButton || withBothButtons) && (
        <div className={styles['close-button']}>
          <CallToActionContainer
            icon={{ url: URLS.CROSS_BLACK, alt: t('action.closeModal') }}
            onClick={onCloseClick} kind="tertiary"
          />
        </div>
      )}
    </div>
  );
};

export default ModalHeader;
